import React, { useState, useCallback, useEffect } from 'react';
import { Search, FileText, Download, Loader2, Package } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import { purchaseReportService, PurchaseReportRow, PurchaseReportSummary, PurchaseReportFilters } from '../../services/purchaseReportService';
import { providerService } from '../../services/providerService';
import { Provider } from '../../types/provider';

// ─── Helpers ─────────────────────────────────────────────────────

const formatNumber = (value?: number) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { maximumFractionDigits: 4 })
    : '0';

const getFirstOfCurrentMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const getToday = () => new Date().toISOString().split('T')[0];

const getPageRange = (current: number, total: number): (number | '...')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

// ─── Component ───────────────────────────────────────────────────

export const PurchaseReportView: React.FC = () => {
  // Filters
  const [dateFrom, setDateFrom] = useState(getFirstOfCurrentMonth());
  const [dateTo, setDateTo] = useState(getToday());
  const [selectedProviderId, setSelectedProviderId] = useState<number | undefined>(undefined);

  // Providers for dropdown
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);

  // Report data
  const [data, setData] = useState<PurchaseReportRow[]>([]);
  const [summary, setSummary] = useState<PurchaseReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  // Export states
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Load providers
  useEffect(() => {
    const loadProviders = async () => {
      try {
        setProvidersLoading(true);
        const result = await providerService.getProviders({ page: 1, pageSize: 500 });
        setProviders(result.data);
      } catch {
        // Silent
      } finally {
        setProvidersLoading(false);
      }
    };
    loadProviders();
  }, []);

  const buildFilters = useCallback((): PurchaseReportFilters => ({
    date_from: dateFrom,
    date_to: dateTo,
    provider_id: selectedProviderId,
    page,
    pageSize,
  }), [dateFrom, dateTo, selectedProviderId, page]);

  const loadReport = useCallback(async (targetPage?: number) => {
    try {
      setLoading(true);
      setError(null);
      const filters = buildFilters();
      if (targetPage !== undefined) filters.page = targetPage;
      const result = await purchaseReportService.getReport(filters);
      setData(result.data);
      setSummary(result.summary);
      setPage(result.pagination.page);
      setTotalPages(result.pagination.totalPages);
      setTotal(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  const handleSearch = () => {
    setPage(1);
    loadReport(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadReport(newPage);
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      await purchaseReportService.downloadPdf({
        date_from: dateFrom,
        date_to: dateTo,
        provider_id: selectedProviderId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao exportar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      await purchaseReportService.downloadExcel({
        date_from: dateFrom,
        date_to: dateTo,
        provider_id: selectedProviderId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao exportar Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <PermissionGuard permission="reports.purchases">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-h1 flex items-center gap-2">
              <Package size={24} /> Relatório de Compras
            </h1>
            <p className="text-slate-600 text-sm">Consolidação de produtos adquiridos por período e fornecedor</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportPdf}
              disabled={exportingPdf || data.length === 0}
              icon={exportingPdf ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            >
              PDF
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportExcel}
              disabled={exportingExcel || data.length === 0}
              icon={exportingExcel ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            >
              Excel
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader title="Filtros" />
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Data Início *</label>
                <DateInputBR value={dateFrom} onChange={setDateFrom} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Data Fim *</label>
                <DateInputBR value={dateTo} onChange={setDateTo} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fornecedor</label>
                <select
                  value={selectedProviderId ?? ''}
                  onChange={(e) => setSelectedProviderId(e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  disabled={providersLoading}
                >
                  <option value="">Todos os fornecedores</option>
                  {providers.map((p) => (
                    <option key={p.provider_id} value={p.provider_id}>
                      {p.trade_name || p.legal_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button onClick={handleSearch} disabled={loading || !dateFrom || !dateTo} icon={<Search size={16} />} className="w-full">
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Summary */}
        {summary && data.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-slate-500">Produtos Distintos</p>
                <p className="text-2xl font-bold text-slate-800">{formatNumber(summary.total_products)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-slate-500">Qtd. Total Pedida</p>
                <p className="text-2xl font-bold text-emerald-600">{formatNumber(summary.total_quantity)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-slate-500">Qtd. Total Entregue</p>
                <p className="text-2xl font-bold text-blue-600">{formatNumber(summary.total_delivered_quantity)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Results Table */}
        {data.length > 0 && (
          <Card>
            <CardHeader
              title={`Resultado (${total} produto${total !== 1 ? 's' : ''})`}
            />
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <tr>
                      <th className="text-left">SKU</th>
                      <th className="text-left">Descrição</th>
                      <th className="text-right">Qtd. Pedida</th>
                      <th className="text-right">Qtd. Entregue</th>
                      <th className="text-left">Pedidos de Compra</th>
                    </tr>
                  </TableHeader>
                  <tbody>
                    {data.map((row) => (
                      <TableRow key={row.product_id}>
                        <TableCell className="font-mono text-sm">{row.sku}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell className="text-right font-medium">{formatNumber(row.total_quantity)}</TableCell>
                        <TableCell className="text-right font-medium">{formatNumber(row.total_delivered_quantity)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {row.purchase_order_ids.map((poId) => (
                              <span
                                key={poId}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                              >
                                #{poId}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {!loading && data.length === 0 && !error && (
          <Card>
            <CardContent className="py-12 text-center">
              <Package size={48} className="mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">Selecione um período e clique em "Buscar" para gerar o relatório</p>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              Anterior
            </Button>
            {getPageRange(page, totalPages).map((p, idx) =>
              p === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => handlePageChange(p as number)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    p === page
                      ? 'bg-emerald-600 text-white font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {p}
                </button>
              )
            )}
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
            >
              Próxima
            </Button>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};
